<script>
import LegacyUpload from "../upload/Upload.vue";
import UploadV2Picker from "./UploadV2Picker.vue";
import { uploadResumable } from "../../../mixins/fileUpload";
import PropertiesSection from "../../common/PropertiesSection.vue";
import KeywordSelector from "../../common/KeywordSelector.vue";
import {
  UPLOAD_V2_CREATE_BATCH_SIZE,
  UPLOAD_V2_FILE_CONCURRENCY,
  UPLOAD_V2_MAX_FILES_PER_JOB,
  UPLOAD_V2_WARN_FILES_PER_JOB,
  UPLOAD_CHUNKED_MIN_SIZE,
} from "../../../util/constants";
import {
  buildUploadManifest,
  CLIENT_UPLOAD_STATUS,
  manifestForApi,
  UPLOAD_ENTRY_TYPE,
} from "../../../util/uploadManifest";
import {
  cancelLocalUploadJob,
  clearLocalUploadLease,
  isUploadJobCancellationRequested,
  markLocalUploadJobCancelling,
  persistUploadJob,
  prepareLocalUploadJobRetry,
  startLocalUploadLease,
  touchLocalUploadLease,
  getUploadJobLocalEntrySummary,
  updateUploadEntries,
  updateUploadEntry,
  updateUploadJob,
} from "../../../util/uploadIndexedDb";
import {
  uploadDiagnosticDurationMs,
  uploadDiagnosticLog,
  uploadDiagnosticLogThrottled,
  uploadDiagnosticStart,
} from "../../../util/uploadDiagnostics";
import { classifyUploadError, isTransientUploadError, uploadJobMixins } from "../../../mixins/uploadJob";
import { enqueueUpload, publishEntryActivity, requestUploadCancel, uploadManagerState } from "../../../util/uploadManager";
import { FEATURE_UPLOAD_STORAGE_PREFLIGHT } from "../../../util/featureFlags";

const LOCAL_LEASE_REFRESH_INTERVAL_MS = 5000;
const LOCAL_PROGRESS_WRITE_THROTTLE_MS = 2000;
const LOCAL_PROGRESS_BATCH_DELAY_MS = 2000;
const LOCAL_STATUS_BATCH_DELAY_MS = 250;
const LOCAL_PROGRESS_WRITE_MIN_PERCENT_DELTA = 2;
const LOCAL_PROGRESS_WRITE_MIN_BYTE_DELTA = 1024 * 1024;
const FINALISE_BATCH_SIZE = 50;
const FINALISE_BATCH_DELAY_MS = 500;
const CREATE_PREFETCH_MIN_BATCHES = 1;
const CREATE_PREFETCH_MAX_BATCHES = 2;
const UPLOAD_TIMING_SAMPLE_LIMIT = 50;
const UPLOAD_WORKER_WATCHDOG_INTERVAL_MS = 15000;
const UPLOAD_WORKER_STALL_WARN_MS = 30000;
const CANCEL_TELEMETRY_TIMEOUT_MS = 1500;
const STORAGE_PREFLIGHT_STATUS = {
  PENDING: "pending",
  CHECKING: "checking",
  PASSED: "passed",
  FAILED: "failed",
};
const UPLOAD_TRANSFER_RATE_TERMINAL_STATUSES = new Set([
  CLIENT_UPLOAD_STATUS.COMPLETE,
  CLIENT_UPLOAD_STATUS.ERROR,
  CLIENT_UPLOAD_STATUS.CANCELLED,
  CLIENT_UPLOAD_STATUS.TIMED_OUT,
  CLIENT_UPLOAD_STATUS.ABANDONED,
]);

function yieldToBrowser() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function shouldBypassStoragePreflight() {
  return !FEATURE_UPLOAD_STORAGE_PREFLIGHT || process.env.NODE_ENV === "development";
}

function withTimeout(promise, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

function isStoppedUploadJobError(error) {
  const message = String(error?.message || error || "").toLowerCase();
  return message.includes("abandon") ||
    message.includes("cancelled") ||
    message.includes("canceled") ||
    message.includes("timed out") ||
    message.includes("not active") ||
    message.includes("terminal") ||
    message.includes("already complete");
}

const EmptyFolderPicker = {
  name: "UploadV2EmptyFolderPicker",
  render(h) {
    return h();
  },
};

function createCancellableRunner(run, cancel) {
  let cancelled = false;
  return {
    run: async () => {
      const result = await run(() => cancelled);
      return { result, cancelled };
    },
    cancel: async () => {
      cancelled = true;
      await cancel?.();
    },
  };
}

export default {
  name: "UploadV2",
  extends: LegacyUpload,
  components: {
    FilePicker: UploadV2Picker,
    FolderPicker: EmptyFolderPicker,
    PropertiesSection,
    KeywordSelector,
  },
  mixins: [uploadJobMixins],
  data() {
    return {
      uploadSurfaceClass: "upload-v2",
      uploadJobId: null,
      uploadManifest: null,
      uploadLeaseOwnerId: null,
      uploadLeaseTimer: null,
      uploadProgressWriteTimes: {},
      uploadProgressWriteSnapshots: {},
      pendingUploadProgress: {},
      uploadProgressFlushTimer: null,
      pendingUploadStatuses: {},
      uploadStatusFlushTimer: null,
      localJobSummary: null,
      uploadTimingSummary: null,
      uploadWorkerWatchdogTimer: null,
      uploadWorkerState: null,
      managerUploadId: null,
      retryManifest: null,
      storagePreflightStatus: shouldBypassStoragePreflight() || process.env.NODE_ENV === "test"
        ? STORAGE_PREFLIGHT_STATUS.PASSED
        : STORAGE_PREFLIGHT_STATUS.PENDING,
      storagePreflightError: null,
    };
  },
  computed: {
    showUploadSetupControls() {
      return this.state < this.STATE.UPLOADING;
    },
    uploadStoragePreflightReady() {
      return this.storagePreflightStatus === STORAGE_PREFLIGHT_STATUS.PASSED;
    },
    uploadStoragePreflightChecking() {
      return this.storagePreflightStatus === STORAGE_PREFLIGHT_STATUS.CHECKING ||
        this.storagePreflightStatus === STORAGE_PREFLIGHT_STATUS.PENDING;
    },
    uploadStoragePreflightFailed() {
      return this.storagePreflightStatus === STORAGE_PREFLIGHT_STATUS.FAILED;
    },
    uploadPickerDisabled() {
      return !this.uploadStoragePreflightReady;
    },
    activeManagerUpload() {
      return uploadManagerState.activeUpload;
    },
  },
  watch: {
    activeManagerUpload: {
      handler(upload) {
        this.syncManagerUploadState(upload);
      },
      deep: true,
    },
  },
  methods: {
    validateUploadV2FileCount(files = [], { showLargeSelectionWarning = true } = {}) {
      if (!this.uploadStoragePreflightReady) {
        this.showStoragePreflightError();
        return false;
      }
      const fileCount = files.length;
      if (fileCount <= 0) {
        this.pendingFiles = [];
        this.zeroByteFiles = [];
        this.oversizedFiles = [];
        this.zeroByteFileStats = null;
        this.chosenFiles = [];
        this.collectionFileSize = 0;
        this.pickerResetKey += 1;
        this.setState(this.STATE.FILE_PICK);
        this.showWarning(this.$t("upload-empty-selection"), { expiryTime: 30000 });
        return false;
      }
      if (fileCount > UPLOAD_V2_MAX_FILES_PER_JOB) {
        this.pendingFiles = [];
        this.zeroByteFiles = [];
        this.oversizedFiles = [];
        this.zeroByteFileStats = null;
        this.chosenFiles = [];
        this.collectionFileSize = 0;
        this.pickerResetKey += 1;
        this.setState(this.STATE.FILE_PICK);
        this.showError(this.$t("upload-too-many-files", {
          count: fileCount,
          max: UPLOAD_V2_MAX_FILES_PER_JOB,
        }), { expiryTime: 30000 });
        uploadDiagnosticLog("warn", "upload-selection-too-many-files", {
          fileCount,
          maxFilesPerJob: UPLOAD_V2_MAX_FILES_PER_JOB,
        });
        return false;
      }
      if (showLargeSelectionWarning && fileCount >= UPLOAD_V2_WARN_FILES_PER_JOB) {
        this.showWarning(this.$t("upload-many-files-warning", {
          count: fileCount,
          max: UPLOAD_V2_MAX_FILES_PER_JOB,
        }), { expiryTime: 30000 });
        uploadDiagnosticLog("warn", "upload-selection-large-file-count", {
          fileCount,
          warnFilesPerJob: UPLOAD_V2_WARN_FILES_PER_JOB,
          maxFilesPerJob: UPLOAD_V2_MAX_FILES_PER_JOB,
        });
      }
      return true;
    },
    storagePreflightMessage() {
      return this.$t("upload-storage-preflight-failed");
    },
    showStoragePreflightError() {
      const message = this.storagePreflightError?.userMessage || this.storagePreflightMessage();
      this.showError(message, { expiryTime: 30000 });
    },
    async runStoragePreflight({ notifyOnFailure = true } = {}) {
      if (shouldBypassStoragePreflight()) {
        this.storagePreflightStatus = STORAGE_PREFLIGHT_STATUS.PASSED;
        this.storagePreflightError = null;
        uploadDiagnosticLog("info", "upload-storage-preflight-skipped", {
          reason: "development-mode",
          targetFolderId: this.folderId,
        });
        return true;
      }
      if (this.storagePreflightStatus === STORAGE_PREFLIGHT_STATUS.CHECKING) {
        return false;
      }
      this.storagePreflightStatus = STORAGE_PREFLIGHT_STATUS.CHECKING;
      this.storagePreflightError = null;
      let preflight = null;
      const startedAt = uploadDiagnosticStart();
      try {
        uploadDiagnosticLog("info", "upload-storage-preflight-start", { targetFolderId: this.folderId });
        preflight = await this.callAPI("item/upload-jobs/storage-preflight", {
          method: "POST",
          showLoader: false,
        });
        const byteCount = Math.max(1, Number(preflight?.byteCount || 16));
        const contentType = preflight?.contentType || "application/octet-stream";
        const sessionResponse = await fetch(preflight.startUri, {
          method: "POST",
          headers: {
            "X-Goog-Resumable": "start",
            "X-Upload-Content-Type": contentType,
            "X-Upload-Content-Length": String(byteCount),
            "Content-Type": contentType,
          },
          body: null,
        });
        if (!sessionResponse.ok) {
          throw new Error(`GCS preflight session start failed with status ${sessionResponse.status}`);
        }
        const sessionUri = sessionResponse.headers.get("Location");
        if (!sessionUri) {
          throw new Error("GCS preflight session start did not return a Location header");
        }
        const payload = new Blob([new Uint8Array(byteCount)], { type: contentType });
        await uploadResumable({
          url: sessionUri,
          file: payload,
          contentType,
          chunkSize: byteCount,
          isCancelled: () => false,
          maxRetries: 0,
          onChunkUpload: () => null,
        });
        this.storagePreflightStatus = STORAGE_PREFLIGHT_STATUS.PASSED;
        uploadDiagnosticLog("info", "upload-storage-preflight-complete", {
          targetFolderId: this.folderId,
          durationMs: uploadDiagnosticDurationMs(startedAt),
        });
        return true;
      } catch (error) {
        this.storagePreflightStatus = STORAGE_PREFLIGHT_STATUS.FAILED;
        this.storagePreflightError = {
          error,
          userMessage: this.storagePreflightMessage(),
        };
        uploadDiagnosticLog("warn", "upload-storage-preflight-failed", {
          targetFolderId: this.folderId,
          durationMs: uploadDiagnosticDurationMs(startedAt),
        }, error);
        if (notifyOnFailure) {
          this.showStoragePreflightError();
        }
        return false;
      } finally {
        if (preflight?.objectName) {
          await this.callAPI("item/upload-jobs/storage-preflight/complete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            showLoader: false,
            showErrorNotification: false,
            body: JSON.stringify({ objectName: preflight.objectName }),
          }).catch((cleanupError) => {
            uploadDiagnosticLog("warn", "upload-storage-preflight-cleanup-failed", {
              targetFolderId: this.folderId,
            }, cleanupError);
          });
        }
      }
    },
    applyFilesSelection(files, stats = null) {
      if (!this.validateUploadV2FileCount(files || [])) {
        return;
      }
      LegacyUpload.methods.applyFilesSelection.call(this, files, stats);
    },
    emitUploadJobChanged(updates = {}) {
      if (!this.uploadJobId) {
        return;
      }
      const retentionSchedule = this.selectedRetentionSchedule || {};
      this.$emit("upload-job-changed", {
        id: this.uploadJobId,
        targetFolderId: this.folderId,
        targetFolderLabel: this.folder?.name || this.folder?.label || "",
        retentionSchedule: retentionSchedule.id || "",
        retentionScheduleLabel: this.formatRetentionScheduleLabel(retentionSchedule),
        retentionExpiryDate: this.estimateRetentionExpiryDate(retentionSchedule),
        secretUpload: !!this.isSecret,
        keywords: [...(this.currentSubjects || [])],
        metadata: [...(this.currentMetadata || [])],
        status: "IN_PROGRESS",
        ...updates,
      });
    },
    formatRetentionScheduleLabel(retentionSchedule) {
      if (!retentionSchedule?.id) {
        return "-";
      }
      return retentionSchedule.period || retentionSchedule.id;
    },
    estimateRetentionExpiryDate(retentionSchedule) {
      if (!retentionSchedule?.id) {
        return null;
      }
      if (retentionSchedule.expiryDriven === false || retentionSchedule.supersededRetention) {
        return null;
      }
      const date = new Date();
      if (retentionSchedule.period?.endsWith("D")) {
        date.setDate(date.getDate() + Number(retentionSchedule.daysUntilExpiry || 0));
      } else if (retentionSchedule.period?.endsWith("M")) {
        date.setMonth(date.getMonth() + Number(retentionSchedule.monthsUntilExpiry || 0));
      } else if (retentionSchedule.period?.endsWith("Y")) {
        date.setFullYear(date.getFullYear() + Number(retentionSchedule.yearsUntilExpiry || 0));
      } else {
        return null;
      }
      return date.toISOString().slice(0, 10);
    },
    async startLocalLease(jobId) {
      this.stopLocalLease();
      this.uploadLeaseOwnerId = await startLocalUploadLease(jobId).catch((error) => {
        uploadDiagnosticLog("warn", "local-lease-start-failed", { uploadJobId: jobId }, error);
        console.error(error);
        return null;
      });
      if (!this.uploadLeaseOwnerId) {
        return;
      }
      this.uploadLeaseTimer = window.setInterval(() => {
        touchLocalUploadLease(jobId, this.uploadLeaseOwnerId).catch((error) => {
          uploadDiagnosticLog("warn", "local-lease-touch-failed", {
            uploadJobId: jobId,
            leaseOwnerId: this.uploadLeaseOwnerId,
          }, error);
          console.error(error);
        });
      }, LOCAL_LEASE_REFRESH_INTERVAL_MS);
    },
    stopLocalLease() {
      if (this.uploadLeaseTimer) {
        window.clearInterval(this.uploadLeaseTimer);
        this.uploadLeaseTimer = null;
      }
    },
    async clearLocalLease() {
      this.stopLocalLease();
      if (this.uploadJobId && this.uploadLeaseOwnerId) {
        await clearLocalUploadLease(this.uploadJobId, this.uploadLeaseOwnerId).catch((error) => {
          uploadDiagnosticLog("warn", "local-lease-clear-failed", {
            uploadJobId: this.uploadJobId,
            leaseOwnerId: this.uploadLeaseOwnerId,
          }, error);
          console.error(error);
        });
      }
      this.uploadLeaseOwnerId = null;
    },
    stopProgressFlushTimer() {
      if (this.uploadProgressFlushTimer) {
        window.clearTimeout(this.uploadProgressFlushTimer);
        this.uploadProgressFlushTimer = null;
      }
    },
    scheduleProgressFlush() {
      if (this.uploadProgressFlushTimer) {
        return;
      }
      this.uploadProgressFlushTimer = window.setTimeout(() => {
        this.uploadProgressFlushTimer = null;
        this.flushPendingEntryProgress().catch(console.error);
      }, LOCAL_PROGRESS_BATCH_DELAY_MS);
    },
    async flushPendingEntryProgress() {
      this.stopProgressFlushTimer();
      const pending = this.pendingUploadProgress;
      this.pendingUploadProgress = {};
      const byJobId = Object.values(pending).reduce((grouped, progress) => {
        if (!grouped[progress.jobId]) {
          grouped[progress.jobId] = [];
        }
        grouped[progress.jobId].push({
          clientEntryId: progress.clientEntryId,
          updates: {
            uploadedBytes: progress.uploadedBytes,
            progressPercent: progress.progressPercent,
            progressUpdatedAt: progress.progressUpdatedAt,
          },
        });
        return grouped;
      }, {});
      await Promise.all(Object.entries(byJobId).map(([jobId, entryUpdates]) =>
        updateUploadEntries(Number(jobId), entryUpdates).catch((error) => {
          uploadDiagnosticLog("warn", "entry-progress-local-batch-update-failed", {
            uploadJobId: Number(jobId),
            entryCount: entryUpdates.length,
          }, error);
          console.error(error);
        })
      ));
      if (this.uploadJobId && this.localJobSummary) {
        await this.persistLocalJobSummary(this.uploadJobId).catch(console.error);
      }
    },
    stopStatusFlushTimer() {
      if (this.uploadStatusFlushTimer) {
        window.clearTimeout(this.uploadStatusFlushTimer);
        this.uploadStatusFlushTimer = null;
      }
    },
    browserUploadMemorySnapshot() {
      const memory = typeof performance !== "undefined" ? performance.memory : null;
      if (!memory) {
        return null;
      }
      return {
        usedHeapMB: Math.round(memory.usedJSHeapSize / 1048576),
        totalHeapMB: Math.round(memory.totalJSHeapSize / 1048576),
        heapLimitMB: Math.round(memory.jsHeapSizeLimit / 1048576),
      };
    },
    stopUploadWorkerWatchdog() {
      if (this.uploadWorkerWatchdogTimer) {
        window.clearInterval(this.uploadWorkerWatchdogTimer);
        this.uploadWorkerWatchdogTimer = null;
      }
    },
    touchUploadWorkerActivity(reason, updates = {}) {
      if (!this.uploadWorkerState) {
        return;
      }
      this.uploadWorkerState = {
        ...this.uploadWorkerState,
        ...updates,
        lastActivityAt: Date.now(),
        lastActivityReason: reason,
      };
    },
    startUploadWorkerWatchdog(jobId, totalFiles) {
      this.stopUploadWorkerWatchdog();
      this.uploadWorkerState = {
        jobId,
        totalFiles,
        nextEntryIndex: 0,
        activeStorageUploads: 0,
        completedStorageUploads: 0,
        finaliseInFlight: 0,
        pendingCreateBatches: 0,
        lastActivityAt: Date.now(),
        lastActivityReason: "start",
      };
      this.uploadWorkerWatchdogTimer = window.setInterval(() => {
        const state = this.uploadWorkerState;
        if (!state) {
          return;
        }
        const idleMs = Date.now() - Number(state.lastActivityAt || Date.now());
        const level = idleMs >= UPLOAD_WORKER_STALL_WARN_MS ? "warn" : "debug";
        uploadDiagnosticLog(level, "upload-worker-watchdog", {
          uploadJobId: jobId,
          totalFiles,
          nextEntryIndex: state.nextEntryIndex,
          activeStorageUploads: state.activeStorageUploads,
          completedStorageUploads: state.completedStorageUploads,
          finaliseInFlight: state.finaliseInFlight,
          pendingCreateBatches: state.pendingCreateBatches,
          pendingProgressWrites: Object.keys(this.pendingUploadProgress || {}).length,
          pendingStatusWrites: Object.keys(this.pendingUploadStatuses || {}).length,
          idleMs,
          lastActivityReason: state.lastActivityReason,
          memory: this.browserUploadMemorySnapshot(),
        });
      }, UPLOAD_WORKER_WATCHDOG_INTERVAL_MS);
    },
    scheduleStatusFlush() {
      if (this.uploadStatusFlushTimer) {
        return;
      }
      this.uploadStatusFlushTimer = window.setTimeout(() => {
        this.uploadStatusFlushTimer = null;
        this.flushPendingEntryStatuses().catch(console.error);
      }, LOCAL_STATUS_BATCH_DELAY_MS);
    },
    async flushPendingEntryStatuses() {
      this.stopStatusFlushTimer();
      const pending = this.pendingUploadStatuses || {};
      this.pendingUploadStatuses = {};
      if (!Object.keys(pending).length) {
        return;
      }
      const byJobId = Object.values(pending).reduce((grouped, statusUpdate) => {
        if (!grouped[statusUpdate.jobId]) {
          grouped[statusUpdate.jobId] = [];
        }
        grouped[statusUpdate.jobId].push({
          clientEntryId: statusUpdate.clientEntryId,
          updates: statusUpdate.updates,
        });
        return grouped;
      }, {});
      await Promise.all(Object.entries(byJobId).map(([jobId, entryUpdates]) =>
        updateUploadEntries(Number(jobId), entryUpdates).catch((error) => {
          uploadDiagnosticLog("warn", "entry-status-local-batch-update-failed", {
            uploadJobId: Number(jobId),
            entryCount: entryUpdates.length,
          }, error);
          console.error(error);
        })
      ));
    },
    initialiseLocalJobSummary(manifest) {
      this.localJobSummary = {
        totalEntryCount: manifest.summary.entryCount,
        completedEntryCount: 0,
        failedEntryCount: 0,
        totalFileCount: manifest.summary.fileCount,
        completedFileCount: 0,
        failedFileCount: 0,
        totalFolderCount: manifest.summary.folderCount,
        completedFolderCount: 0,
        failedFolderCount: 0,
        totalBytes: manifest.summary.totalBytes,
        uploadedBytes: 0,
        entryProgress: {},
        terminalEntries: {},
      };
    },
    initialiseLocalJobSummaryFromSnapshot(summary) {
      this.localJobSummary = {
        totalEntryCount: Number(summary?.totalEntryCount || 0),
        completedEntryCount: Number(summary?.completedEntryCount || 0),
        failedEntryCount: Number(summary?.failedEntryCount || 0),
        totalFileCount: Number(summary?.totalFileCount || 0),
        completedFileCount: Number(summary?.completedFileCount || 0),
        failedFileCount: Number(summary?.failedFileCount || 0),
        totalFolderCount: Number(summary?.totalFolderCount || 0),
        completedFolderCount: Number(summary?.completedFolderCount || 0),
        failedFolderCount: Number(summary?.failedFolderCount || 0),
        totalBytes: Number(summary?.totalBytes || 0),
        uploadedBytes: Number(summary?.uploadedBytes || 0),
        entryProgress: {},
        terminalEntries: {},
      };
    },
    localEntryUploadedBytes(entry, status) {
      if (entry.entryType !== UPLOAD_ENTRY_TYPE.FILE) {
        return 0;
      }
      if (status === CLIENT_UPLOAD_STATUS.COMPLETE) {
        return Number(entry.size || 0);
      }
      return this.clampProgress(entry.uploadedBytes, entry.size).uploadedBytes;
    },
    localJobProgressPercent(summary = this.localJobSummary) {
      if (!summary) {
        return null;
      }
      const totalBytes = Number(summary.totalBytes || 0);
      const uploadedBytes = Math.max(0, Math.min(totalBytes || Number.MAX_SAFE_INTEGER, Number(summary.uploadedBytes || 0)));
      if (totalBytes > 0) {
        return Math.max(0, Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)));
      }
      const totalEntries = Number(summary.totalEntryCount || 0);
      if (totalEntries <= 0) {
        return null;
      }
      const completedFolders = Number(summary.completedFolderCount || 0);
      const failedFolders = Number(summary.failedFolderCount || 0);
      const completedFiles = Number(summary.completedFileCount || 0);
      const failedFiles = Number(summary.failedFileCount || 0);
      const completedUnits = completedFolders + failedFolders + completedFiles + failedFiles;
      return Math.max(0, Math.min(100, Math.round((completedUnits / totalEntries) * 100)));
    },
    updateLocalJobSummaryForEntry(entry, previousStatus, nextStatus) {
      if (!this.localJobSummary || !entry?.clientEntryId) {
        return;
      }
      const key = entry.clientEntryId;
      const previousUploaded = Number(this.localJobSummary.entryProgress[key] || 0);
      const nextUploaded = this.localEntryUploadedBytes(entry, nextStatus);
      this.localJobSummary.uploadedBytes = Math.max(
        0,
        this.localJobSummary.uploadedBytes + nextUploaded - previousUploaded
      );
      this.localJobSummary.entryProgress[key] = nextUploaded;

      if (this.localJobSummary.terminalEntries[key]) {
        return;
      }
      if (nextStatus === CLIENT_UPLOAD_STATUS.COMPLETE) {
        this.localJobSummary.terminalEntries[key] = true;
        this.localJobSummary.completedEntryCount += 1;
        if (entry.entryType === UPLOAD_ENTRY_TYPE.FILE) {
          this.localJobSummary.completedFileCount += 1;
        } else if (entry.entryType === UPLOAD_ENTRY_TYPE.FOLDER) {
          this.localJobSummary.completedFolderCount += 1;
        }
      } else if (
        nextStatus === CLIENT_UPLOAD_STATUS.ERROR ||
        nextStatus === CLIENT_UPLOAD_STATUS.CANCELLED ||
        nextStatus === CLIENT_UPLOAD_STATUS.TIMED_OUT ||
        nextStatus === CLIENT_UPLOAD_STATUS.ABANDONED
      ) {
        this.localJobSummary.terminalEntries[key] = true;
        this.localJobSummary.failedEntryCount += 1;
        if (entry.entryType === UPLOAD_ENTRY_TYPE.FILE) {
          this.localJobSummary.failedFileCount += 1;
        } else if (entry.entryType === UPLOAD_ENTRY_TYPE.FOLDER) {
          this.localJobSummary.failedFolderCount += 1;
        }
      }
    },
    async persistLocalJobSummary(jobId, extraUpdates = {}) {
      if (!this.localJobSummary) {
        return;
      }
      const totalBytes = Number(this.localJobSummary.totalBytes || 0);
      const progressPercent = this.localJobProgressPercent();
      await updateUploadJob(jobId, {
        totalEntryCount: this.localJobSummary.totalEntryCount,
        completedEntryCount: this.localJobSummary.completedEntryCount,
        failedEntryCount: this.localJobSummary.failedEntryCount,
        totalFileCount: this.localJobSummary.totalFileCount,
        completedFileCount: this.localJobSummary.completedFileCount,
        failedFileCount: this.localJobSummary.failedFileCount,
        totalFolderCount: this.localJobSummary.totalFolderCount,
        completedFolderCount: this.localJobSummary.completedFolderCount,
        failedFolderCount: this.localJobSummary.failedFolderCount,
        totalBytes,
        uploadedBytes: Math.min(totalBytes || Number.MAX_SAFE_INTEGER, this.localJobSummary.uploadedBytes),
        progressPercent,
        progressUpdatedAt: new Date().toISOString(),
        ...extraUpdates,
      });
      this.emitUploadJobChanged({
        totalEntryCount: this.localJobSummary.totalEntryCount,
        completedEntryCount: this.localJobSummary.completedEntryCount,
        failedEntryCount: this.localJobSummary.failedEntryCount,
        totalFileCount: this.localJobSummary.totalFileCount,
        completedFileCount: this.localJobSummary.completedFileCount,
        failedFileCount: this.localJobSummary.failedFileCount,
        totalFolderCount: this.localJobSummary.totalFolderCount,
        completedFolderCount: this.localJobSummary.completedFolderCount,
        failedFolderCount: this.localJobSummary.failedFolderCount,
        totalBytes,
        uploadedBytes: Math.min(totalBytes || Number.MAX_SAFE_INTEGER, this.localJobSummary.uploadedBytes),
        progressPercent,
        ...extraUpdates,
      });
    },
    clampProgress(uploadedBytes, totalBytes) {
      const total = Math.max(0, Number(totalBytes || 0));
      const uploaded = Math.max(0, Math.min(total || Number.MAX_SAFE_INTEGER, Number(uploadedBytes || 0)));
      return {
        uploadedBytes: uploaded,
        totalBytes: total,
        progressPercent: total > 0 ? Math.max(0, Math.min(100, Math.round((uploaded / total) * 100))) : null,
      };
    },
    persistEntryProgress(jobId, entry, info = {}, { force = false } = {}) {
      if (!jobId || !entry?.clientEntryId || entry.entryType !== UPLOAD_ENTRY_TYPE.FILE) {
        return;
      }
      const progress = this.clampProgress(info.uploadedBytes, info.totalBytes || entry.size);
      const previousStatus = entry.clientStatus;
      entry.uploadedBytes = progress.uploadedBytes;
      entry.progressPercent = progress.progressPercent;
      entry.progressUpdatedAt = new Date().toISOString();
      this.updateLocalJobSummaryForEntry(entry, previousStatus, previousStatus);
      this.recordEntryUploadTransferProgress?.(entry.clientEntryId, progress.uploadedBytes, Date.parse(entry.progressUpdatedAt));
      if (progress.totalBytes > 0 && progress.uploadedBytes >= progress.totalBytes) {
        this.clearEntryUploadTransferRate?.(entry.clientEntryId);
      }
      this.updateOverallUploadByteProgress?.(this.localJobSummary?.uploadedBytes, this.localJobSummary?.totalBytes, { recordRate: false });
      this.publishEntryActivityForEntry(jobId, entry, previousStatus, {
        uploadedBytes: progress.uploadedBytes,
        totalBytes: progress.totalBytes,
        progressPercent: progress.progressPercent,
        lastUpdated: entry.progressUpdatedAt,
      });

      if (!this.uploadProgressWriteTimes) {
        this.uploadProgressWriteTimes = {};
      }
      if (!this.uploadProgressWriteSnapshots) {
        this.uploadProgressWriteSnapshots = {};
      }
      if (!this.pendingUploadProgress) {
        this.pendingUploadProgress = {};
      }

      const key = `${jobId}:${entry.clientEntryId}`;
      const now = Date.now();
      const lastWriteAt = this.uploadProgressWriteTimes[key] || 0;
      const lastSnapshot = this.uploadProgressWriteSnapshots[key] || {};
      const byteDelta = Math.abs(progress.uploadedBytes - Number(lastSnapshot.uploadedBytes || 0));
      const percentDelta = progress.progressPercent === null || lastSnapshot.progressPercent === null || lastSnapshot.progressPercent === undefined
        ? 0
        : Math.abs(progress.progressPercent - Number(lastSnapshot.progressPercent || 0));
      const shouldPersistProgress = force ||
        progress.uploadedBytes >= progress.totalBytes ||
        now - lastWriteAt >= LOCAL_PROGRESS_WRITE_THROTTLE_MS ||
        byteDelta >= LOCAL_PROGRESS_WRITE_MIN_BYTE_DELTA ||
        percentDelta >= LOCAL_PROGRESS_WRITE_MIN_PERCENT_DELTA;
      if (!shouldPersistProgress) {
        return;
      }
      this.uploadProgressWriteTimes[key] = now;
      this.uploadProgressWriteSnapshots[key] = {
        uploadedBytes: progress.uploadedBytes,
        progressPercent: progress.progressPercent,
      };
      this.pendingUploadProgress[key] = {
        jobId,
        clientEntryId: entry.clientEntryId,
        uploadedBytes: progress.uploadedBytes,
        progressPercent: progress.progressPercent,
        progressUpdatedAt: entry.progressUpdatedAt,
      };
      if (force) {
        this.flushPendingEntryProgress().catch(console.error);
        return;
      }
      this.scheduleProgressFlush();
    },
    resetUploadTimingSummary() {
      this.uploadTimingSummary = {
        createFile: this.emptyUploadTimingStage(),
        storageUpload: this.emptyUploadTimingStage(),
        finaliseFile: this.emptyUploadTimingStage(),
        queueWait: this.emptyUploadTimingStage(),
        effectiveConcurrency: {
          peak: 0,
          samples: [],
        },
      };
    },
    emptyUploadTimingStage() {
      return {
        count: 0,
        totalMs: 0,
        maxMs: 0,
        samples: [],
      };
    },
    recordUploadTiming(stage, timing = {}) {
      if (!this.uploadTimingSummary) {
        this.resetUploadTimingSummary();
      }
      const target = this.uploadTimingSummary[stage];
      const durationMs = Math.max(0, Math.round(Number(timing.durationMs || 0)));
      if (!target || !Number.isFinite(durationMs)) {
        return;
      }
      target.count += 1;
      target.totalMs += durationMs;
      target.maxMs = Math.max(target.maxMs, durationMs);
      if (target.samples.length < UPLOAD_TIMING_SAMPLE_LIMIT) {
        target.samples.push({
          durationMs,
          size: timing.size,
          clientEntryId: timing.clientEntryId,
        });
      }
    },
    recordEffectiveConcurrency(activeCount, queuedCount = null) {
      if (!this.uploadTimingSummary) {
        this.resetUploadTimingSummary();
      }
      const concurrency = this.uploadTimingSummary.effectiveConcurrency;
      concurrency.peak = Math.max(concurrency.peak, Number(activeCount || 0));
      if (concurrency.samples.length < UPLOAD_TIMING_SAMPLE_LIMIT) {
        concurrency.samples.push({
          atMs: Date.now(),
          activeCount: Number(activeCount || 0),
          queuedCount,
        });
      }
    },
      uploadTimingTelemetry() {
        if (!this.uploadTimingSummary) {
          return {};
        }
      const summarizeStage = (stage) => ({
        count: stage.count,
        averageMs: stage.count ? Math.round(stage.totalMs / stage.count) : null,
        maxMs: stage.maxMs || null,
        samples: stage.samples,
      });
        return {
          fileConcurrency: UPLOAD_V2_FILE_CONCURRENCY,
          createBatchSize: UPLOAD_V2_CREATE_BATCH_SIZE,
          finaliseBatchSize: FINALISE_BATCH_SIZE,
          createFile: summarizeStage(this.uploadTimingSummary.createFile),
        storageUpload: summarizeStage(this.uploadTimingSummary.storageUpload),
        finaliseFile: summarizeStage(this.uploadTimingSummary.finaliseFile),
        queueWait: summarizeStage(this.uploadTimingSummary.queueWait),
        effectiveConcurrency: this.uploadTimingSummary.effectiveConcurrency,
      };
    },
    publishEntryActivityForEntry(jobId, entry, clientStatus, updates = {}) {
      publishEntryActivity({
        jobId,
        localUploadId: this.managerUploadId || uploadManagerState.activeUpload?.localUploadId || null,
        clientEntryId: entry?.clientEntryId,
        entryType: entry?.entryType,
        relativePath: entry?.relativePath || entry?.sourceItem?.path || entry?.sourceItem?.file?.name,
        name: entry?.name || entry?.sourceItem?.file?.name || entry?.relativePath,
        status: clientStatus,
        uploadedBytes: updates.uploadedBytes ?? entry?.uploadedBytes ?? 0,
        totalBytes: updates.totalBytes ?? entry?.size ?? 0,
        progressPercent: updates.progressPercent ?? entry?.progressPercent ?? null,
        errorMessage: updates.errorMessage ?? entry?.errorMessage ?? "",
        lastUpdated: updates.lastUpdated || entry?.lastUpdatedAt || entry?.progressUpdatedAt,
      });
    },
    applyEntryStatus(entry, clientStatus, updates = {}, jobId = null) {
      const previousStatus = entry.clientStatus;
      entry.clientStatus = clientStatus;
      entry.lastUpdatedAt = new Date().toISOString();
      if (updates.itemId !== undefined) {
        entry.itemId = updates.itemId;
      }
      if (updates.uploadId !== undefined) {
        entry.uploadId = updates.uploadId;
      }
      if (updates.errorCode !== undefined) {
        entry.errorCode = updates.errorCode;
      }
      if (updates.errorMessage !== undefined) {
        entry.errorMessage = updates.errorMessage;
      }
      if (updates.uploadedBytes !== undefined || updates.totalBytes !== undefined) {
        const progress = this.clampProgress(updates.uploadedBytes, updates.totalBytes || entry.size);
        entry.uploadedBytes = progress.uploadedBytes;
        entry.progressPercent = progress.progressPercent;
        entry.progressUpdatedAt = new Date().toISOString();
      }
      this.updateLocalJobSummaryForEntry(entry, previousStatus, clientStatus);
      if (UPLOAD_TRANSFER_RATE_TERMINAL_STATUSES.has(clientStatus)) {
        this.clearEntryUploadTransferRate?.(entry.clientEntryId);
      }
      this.updateOverallUploadByteProgress?.(this.localJobSummary?.uploadedBytes, this.localJobSummary?.totalBytes, { recordRate: false });
      if (jobId) {
        this.publishEntryActivityForEntry(jobId, entry, clientStatus, {
          ...updates,
          lastUpdated: entry.lastUpdatedAt,
        });
      }
    },
    entryProgressPersistUpdates(entry) {
      if (entry?.entryType !== UPLOAD_ENTRY_TYPE.FILE) {
        return {};
      }
      const hasUploadedBytes = entry.uploadedBytes !== undefined && entry.uploadedBytes !== null;
      const hasProgressPercent = entry.progressPercent !== undefined && entry.progressPercent !== null;
      if (!hasUploadedBytes && !hasProgressPercent) {
        return {};
      }
      return {
        ...(hasUploadedBytes ? { uploadedBytes: entry.uploadedBytes } : {}),
        ...(hasProgressPercent ? { progressPercent: entry.progressPercent } : {}),
        ...(entry.progressUpdatedAt ? { progressUpdatedAt: entry.progressUpdatedAt } : {}),
      };
    },
    async persistEntryStatus(jobId, entry, clientStatus, updates = {}) {
      this.applyEntryStatus(entry, clientStatus, updates, jobId);
      await updateUploadEntry(jobId, entry.clientEntryId, {
        clientStatus,
        itemId: entry.itemId,
        uploadId: entry.uploadId,
        errorCode: entry.errorCode,
        errorMessage: entry.errorMessage,
        ...this.entryProgressPersistUpdates(entry),
        incrementAttempt: clientStatus === CLIENT_UPLOAD_STATUS.CREATING_FILE || clientStatus === CLIENT_UPLOAD_STATUS.CREATING_FOLDER,
      }).catch((error) => {
        uploadDiagnosticLog("warn", "entry-local-status-persist-failed", {
          uploadJobId: jobId,
          uploadJobLogId: entry.serverLogId,
          clientEntryId: entry.clientEntryId,
          clientStatus,
        }, error);
        console.error(error);
      });
      const level = clientStatus === CLIENT_UPLOAD_STATUS.COMPLETE ? "debug" : "debug";
      uploadDiagnosticLog(level, "entry-client-status", {
        uploadJobId: jobId,
        uploadJobLogId: entry.serverLogId,
        clientEntryId: entry.clientEntryId,
        clientStatus,
        itemId: entry.itemId,
        size: entry.size,
      });
    },
    queueEntryStatusPersist(jobId, entry, clientStatus, updates = {}) {
      this.applyEntryStatus(entry, clientStatus, updates, jobId);
      if (!this.pendingUploadStatuses) {
        this.pendingUploadStatuses = {};
      }
      const key = `${jobId}:${entry.clientEntryId}`;
      this.pendingUploadStatuses[key] = {
        jobId,
        clientEntryId: entry.clientEntryId,
        updates: {
          clientStatus,
          itemId: entry.itemId,
          uploadId: entry.uploadId,
          errorCode: entry.errorCode,
          errorMessage: entry.errorMessage,
          ...this.entryProgressPersistUpdates(entry),
          incrementAttempt: clientStatus === CLIENT_UPLOAD_STATUS.CREATING_FILE || clientStatus === CLIENT_UPLOAD_STATUS.CREATING_FOLDER,
        },
      };
      this.scheduleStatusFlush();
      uploadDiagnosticLog("debug", "entry-client-status", {
        uploadJobId: jobId,
        uploadJobLogId: entry.serverLogId,
        clientEntryId: entry.clientEntryId,
        clientStatus,
        itemId: entry.itemId,
        size: entry.size,
        localPersist: "batched",
      });
    },
    applyUploadJobMappings(manifest, response) {
      const mappings = new Map((response?.entries || []).map((entry) => [entry.clientEntryId, entry]));
      manifest.entries.forEach((entry) => {
        const mapping = mappings.get(entry.clientEntryId);
        if (mapping) {
          entry.serverLogId = mapping.uploadJobLogId;
          entry.clientStatus = mapping.status || entry.clientStatus;
        }
      });
      return manifest;
    },
    buildUploadJobManifest() {
      if (this.retryManifest) {
        return this.retryManifest;
      }
      const startedAt = uploadDiagnosticStart();
      const manifest = buildUploadManifest({
        folderId: this.folderId,
        items: this.chosenFiles,
        isSecret: this.isSecret,
        retentionSchedule: this.selectedRetentionSchedule.id,
        tags: this.currentSubjects,
        metadata: this.currentMetadata,
      });
      uploadDiagnosticLog("info", "upload-manifest-built", {
        targetFolderId: manifest.folderId,
        schemaVersion: manifest.schemaVersion,
        entryCount: manifest.summary.entryCount,
        fileCount: manifest.summary.fileCount,
        folderCount: manifest.summary.folderCount,
        totalBytes: manifest.summary.totalBytes,
        durationMs: uploadDiagnosticDurationMs(startedAt),
      });
      return manifest;
    },
    async createAndPersistUploadJob(manifest, progressCallback = null) {
      const startedAt = uploadDiagnosticStart();
      progressCallback?.(0, "", manifest.summary.fileCount, "creating-upload-job", {
        current: 0,
        total: manifest.summary.entryCount,
      });
      uploadDiagnosticLog("info", "upload-job-create-start", {
        targetFolderId: manifest.folderId,
        schemaVersion: manifest.schemaVersion,
        entryCount: manifest.summary.entryCount,
        fileCount: manifest.summary.fileCount,
        folderCount: manifest.summary.folderCount,
        totalBytes: manifest.summary.totalBytes,
      });
      const apiManifest = manifestForApi(manifest);
      const response = manifest.retryOfJobId
        ? await this.retryUploadJob(manifest.retryOfJobId, {
          ...apiManifest,
          sourceAccessMode: manifest.sourceAccessMode,
          sourceAccessStateSummary: manifest.sourceAccessStateSummary,
        })
        : await this.createUploadJob(apiManifest);
      this.uploadJobId = response.jobId;
      this.emitUploadJobChanged({
        status: response.status || "PENDING",
        totalEntryCount: response.entryCount,
        totalFileCount: response.fileCount,
        totalFolderCount: response.folderCount,
        completedEntryCount: 0,
        completedFileCount: 0,
        completedFolderCount: 0,
        failedEntryCount: 0,
        failedFileCount: 0,
        failedFolderCount: 0,
        totalBytes: response.totalBytes,
        uploadedBytes: 0,
      });
      uploadDiagnosticLog("info", "upload-job-create-success", {
        uploadJobId: response.jobId,
        targetFolderId: manifest.folderId,
        schemaVersion: manifest.schemaVersion,
        entryCount: response.entryCount,
        fileCount: response.fileCount,
        folderCount: response.folderCount,
        totalBytes: response.totalBytes,
        durationMs: uploadDiagnosticDurationMs(startedAt),
      });
      this.applyUploadJobMappings(manifest, response);
      const retryingSameJob = manifest.retryOfJobId && Number(response.jobId) === Number(manifest.retryOfJobId);
      if (!retryingSameJob) {
        this.initialiseLocalJobSummary(manifest);
      }
      progressCallback?.(0, "", manifest.summary.fileCount, "saving-upload-job", {
        current: 0,
        total: manifest.summary.entryCount,
      });
      const persistStartedAt = uploadDiagnosticStart();
      const persistOperation = retryingSameJob
        ? prepareLocalUploadJobRetry(response.jobId, manifest, manifest.entries)
        : persistUploadJob({
          jobId: response.jobId,
          manifest,
          entries: manifest.entries,
        });
      await persistOperation.catch((error) => {
        uploadDiagnosticLog("warn", "upload-job-local-persist-failed", {
          uploadJobId: response.jobId,
          targetFolderId: manifest.folderId,
          entryCount: manifest.summary.entryCount,
        }, error);
        console.error(error);
      });
      if (retryingSameJob) {
        const summary = await getUploadJobLocalEntrySummary(response.jobId).catch(() => null);
        if (summary) {
          this.initialiseLocalJobSummaryFromSnapshot(summary);
        } else {
          this.initialiseLocalJobSummary(manifest);
        }
      }
      uploadDiagnosticLog("info", "upload-job-local-persist-complete", {
        uploadJobId: response.jobId,
        durationMs: uploadDiagnosticDurationMs(persistStartedAt),
      });
      this.uploadManifest = manifest;
      return response;
    },
    async isUploadJobCancelled(jobId, isCancelled) {
      if (isCancelled?.()) {
        return true;
      }
      return await isUploadJobCancellationRequested(jobId).catch(() => false);
    },
    async uploadFolderEntriesV2(jobId, manifest, progressCallback, isCancelled) {
      const folderEntries = manifest.entries.filter((entry) => entry.entryType === UPLOAD_ENTRY_TYPE.FOLDER);
      const pathBatches = this.chunkArray(folderEntries, 500);
      let processed = 0;
      let sensitiveDetections = [];

      if (folderEntries.length > 0) {
        progressCallback(0, "", manifest.summary.fileCount, "creating-folders", {
          current: 0,
          total: folderEntries.length,
        });
      }

      for (let batchIndex = 0; batchIndex < pathBatches.length; batchIndex++) {
        if (await this.isUploadJobCancelled(jobId, isCancelled)) {
          return { cancelled: true, sensitiveDetections };
        }
        const batch = pathBatches[batchIndex];
        const batchStartedAt = uploadDiagnosticStart();
        uploadDiagnosticLog("info", "folder-path-batch-start", {
          uploadJobId: jobId,
          batchIndex,
          batchSize: batch.length,
        });
        await Promise.all(batch.map((entry) => this.persistEntryStatus(jobId, entry, CLIENT_UPLOAD_STATUS.CREATING_FOLDER)));
        try {
          const response = await this.createPaths(
            { id: this.folderId },
            [],
            this.currentSubjects,
            this.currentMetadata,
            batch.map((entry) => ({
              path: entry.relativePath,
              uploadJobId: jobId,
              uploadJobLogId: entry.serverLogId,
              clientEntryId: entry.clientEntryId,
            }))
          );
          await Promise.all(batch.map((entry) => this.persistEntryStatus(jobId, entry, CLIENT_UPLOAD_STATUS.COMPLETE)));
          processed += batch.length;
          await this.persistLocalJobSummary(jobId).catch(console.error);
          progressCallback(0, "", manifest.summary.fileCount, "creating-folders", {
            current: processed,
            total: folderEntries.length,
          });
          sensitiveDetections = sensitiveDetections.concat(response?.sensitiveNameDetections || []);
          uploadDiagnosticLog("info", "folder-path-batch-complete", {
            uploadJobId: jobId,
            batchIndex,
            batchSize: batch.length,
            processed,
            durationMs: uploadDiagnosticDurationMs(batchStartedAt),
          });
        } catch (error) {
          await Promise.all(batch.map((entry) => this.recordEntryFailure(jobId, entry, error)));
          uploadDiagnosticLog("warn", "folder-path-batch-failed", {
            uploadJobId: jobId,
            batchIndex,
            batchSize: batch.length,
            uploadJobLogIds: batch.map((entry) => entry.serverLogId).filter(Boolean),
            clientEntryIds: batch.map((entry) => entry.clientEntryId),
            durationMs: uploadDiagnosticDurationMs(batchStartedAt),
          }, error);
          throw error;
        }
      }
      return { cancelled: false, sensitiveDetections };
    },
    async recordEntryFailure(jobId, entry, error) {
      await this.flushPendingEntryStatuses().catch(console.error);
      const classification = classifyUploadError(error);
      const stopped = await this.isUploadJobCancelled(jobId).catch(() => false);
      const stoppedByRemote = isStoppedUploadJobError(error);
      const terminalStatus = stopped || stoppedByRemote
        ? CLIENT_UPLOAD_STATUS.ABANDONED
        : CLIENT_UPLOAD_STATUS.ERROR;
      if (stoppedByRemote) {
        await updateUploadJob(jobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.ABANDONED }).catch(console.error);
      }
      await this.persistEntryStatus(jobId, entry, terminalStatus, {
        errorCode: classification.code,
        errorMessage: classification.message,
      });
      if (!stopped && !stoppedByRemote && !isTransientUploadError(error) && entry.serverLogId) {
        await this.reportUploadEntryError(jobId, entry.serverLogId, error).catch((reportError) => {
          uploadDiagnosticLog("warn", "entry-error-report-failed", {
            uploadJobId: jobId,
            uploadJobLogId: entry.serverLogId,
            clientEntryId: entry.clientEntryId,
            errorCode: classification.code,
          }, reportError);
          console.error(reportError);
        });
      } else if (stopped || stoppedByRemote) {
        uploadDiagnosticLog("info", "entry-error-report-skipped-job-stopped", {
          uploadJobId: jobId,
          uploadJobLogId: entry.serverLogId,
          clientEntryId: entry.clientEntryId,
          errorCode: classification.code,
        });
      }
      uploadDiagnosticLog("warn", "entry-upload-error", {
        uploadJobId: jobId,
        uploadJobLogId: entry.serverLogId,
        clientEntryId: entry.clientEntryId,
        errorCode: classification.code,
      }, error);
    },
    async markRemainingEntriesCancelled(jobId) {
      if (!this.uploadManifest) {
        return;
      }
      const cancellableEntries = this.uploadManifest.entries.filter((entry) => (
        entry.clientStatus !== CLIENT_UPLOAD_STATUS.COMPLETE &&
        entry.clientStatus !== CLIENT_UPLOAD_STATUS.ERROR
      ));
      await Promise.all(cancellableEntries.map((entry) =>
        this.persistEntryStatus(jobId, entry, CLIENT_UPLOAD_STATUS.CANCELLED)
      ));
    },
    async flushCancellationProgressTelemetry(jobId) {
      if (!jobId) {
        return;
      }
      const summary = this.localJobSummary || {};
      await withTimeout(this.flushUploadJobClientTelemetry(jobId, CLIENT_UPLOAD_STATUS.CANCELLED, {
        targetFolderId: this.folderId,
        leaseOwnerId: this.uploadLeaseOwnerId,
        timings: {
          currentPhase: this.currentPhase,
          currentPhaseProgress: this.currentPhaseProgress,
          uploadStages: this.uploadTimingTelemetry?.() || {},
        },
        localIndexedDb: {
          totalEntryCount: summary.totalEntryCount,
          completedEntryCount: summary.completedEntryCount,
          failedEntryCount: summary.failedEntryCount,
          completedFileCount: summary.completedFileCount,
          failedFileCount: summary.failedFileCount,
          completedFolderCount: summary.completedFolderCount,
          failedFolderCount: summary.failedFolderCount,
          uploadedBytes: summary.uploadedBytes,
        },
      }), CANCEL_TELEMETRY_TIMEOUT_MS);
    },
    async createFileBatchForChunk(jobId, chunk, isCancelled = null) {
      const startedAt = uploadDiagnosticStart();
      let response = null;
      const reusableChunkedEntries = new Map(chunk
        .filter((entry) => entry.itemId && this.shouldPreserveChunkedRetryItem(entry))
        .map((entry) => [entry.clientEntryId, {
          clientEntryId: entry.clientEntryId,
          uploadJobLogId: entry.serverLogId,
          success: true,
          id: entry.itemId,
          uploadUrl: null,
          chunkedUploadRequired: true,
        }]));
      const entriesNeedingCreate = chunk.filter((entry) => !reusableChunkedEntries.has(entry.clientEntryId));
      try {
        response = entriesNeedingCreate.length
          ? await this.createFilesBatch(entriesNeedingCreate.map((entry) =>
          this.uploadFileOptionsForEntry(jobId, entry, entry.sourceItem.file)
          ))
          : { entries: [] };
      } catch (error) {
        if (isStoppedUploadJobError(error)) {
          await updateUploadJob(jobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.ABANDONED }).catch(console.error);
        }
        if (isStoppedUploadJobError(error) || await this.isUploadJobCancelled(jobId, isCancelled)) {
          uploadDiagnosticLog("info", "entry-create-file-batch-stopped-before-fallback", {
            uploadJobId: jobId,
            entryCount: chunk.length,
          }, error);
          return new Map();
        }
        uploadDiagnosticLog("warn", "entry-create-file-batch-api-failed-falling-back", {
          uploadJobId: jobId,
          entryCount: chunk.length,
          uploadJobLogIds: chunk.map((entry) => entry.serverLogId).filter(Boolean).slice(0, 50),
          clientEntryIds: chunk.map((entry) => entry.clientEntryId).slice(0, 50),
        }, error);
        response = await this.createFilesIndividuallyForChunk(jobId, entriesNeedingCreate, isCancelled);
      }
      if (await this.isUploadJobCancelled(jobId, isCancelled)) {
        uploadDiagnosticLog("info", "entry-create-file-batch-stopped", {
          uploadJobId: jobId,
          entryCount: chunk.length,
        });
        return new Map();
      }
      const durationMs = uploadDiagnosticDurationMs(startedAt);
      const entriesByClientId = new Map((response?.entries || []).map((entry) => [entry.clientEntryId, entry]));
      reusableChunkedEntries.forEach((entry, clientEntryId) => entriesByClientId.set(clientEntryId, entry));
      chunk.forEach((entry) => {
        const batchEntry = entriesByClientId.get(entry.clientEntryId);
        if (batchEntry?.success) {
          this.recordUploadTiming("createFile", {
            durationMs: Math.round(durationMs / Math.max(1, chunk.length)),
            size: entry.size,
            clientEntryId: entry.clientEntryId,
          });
        }
      });
      uploadDiagnosticLog("info", "entry-create-file-batch-complete", {
        uploadJobId: jobId,
        entryCount: chunk.length,
        successCount: (response?.entries || []).filter((entry) => entry.success).length,
        durationMs,
      });
      return entriesByClientId;
    },
    async createFilesIndividuallyForChunk(jobId, chunk, isCancelled = null) {
      const entries = await Promise.all(chunk.map(async (entry) => {
        if (await this.isUploadJobCancelled(jobId, isCancelled)) {
          return {
            clientEntryId: entry.clientEntryId,
            uploadJobLogId: entry.serverLogId,
            success: false,
            stopped: true,
          };
        }
        try {
          const response = await this.createFile(this.uploadFileOptionsForEntry(jobId, entry, entry.sourceItem.file));
          return {
            clientEntryId: entry.clientEntryId,
            uploadJobLogId: entry.serverLogId,
            success: true,
            ...response,
          };
        } catch (error) {
          if (isStoppedUploadJobError(error)) {
            await updateUploadJob(jobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.ABANDONED }).catch(console.error);
            return {
              clientEntryId: entry.clientEntryId,
              uploadJobLogId: entry.serverLogId,
              success: false,
              stopped: true,
            };
          }
          const classification = classifyUploadError(error);
          uploadDiagnosticLog("warn", "entry-create-file-single-fallback-failed", {
            uploadJobId: jobId,
            uploadJobLogId: entry.serverLogId,
            clientEntryId: entry.clientEntryId,
            errorCode: classification.code,
          }, error);
          return {
            clientEntryId: entry.clientEntryId,
            uploadJobLogId: entry.serverLogId,
            success: false,
            errorCode: classification.code,
            errorMessage: classification.message,
          };
        }
      }));
      return { entries };
    },
    queueFileBatchForUpload(jobId, chunk, chunkIndex) {
      chunk.forEach((entry) => this.applyEntryStatus(entry, CLIENT_UPLOAD_STATUS.QUEUED, {}, jobId));
      updateUploadEntries(jobId, chunk.map((entry) => ({
        clientEntryId: entry.clientEntryId,
        updates: {
          clientStatus: CLIENT_UPLOAD_STATUS.QUEUED,
        },
      }))).catch((error) => {
        uploadDiagnosticLog("warn", "entry-queued-local-batch-update-failed", {
          uploadJobId: jobId,
          chunkIndex,
          entryCount: chunk.length,
        }, error);
        console.error(error);
      });
    },
    async cleanupPrecreatedFileBatch(jobId, chunkIndex, createPromise, keepClientEntryIds = new Set()) {
      if (!createPromise) {
        return;
      }
      try {
        const createdFilesByClientId = await createPromise;
        const createdFiles = Array.from(createdFilesByClientId.values())
          .filter((createdFile) => createdFile?.success && createdFile.id && !keepClientEntryIds.has(createdFile.clientEntryId));
        await Promise.all(createdFiles.map((createdFile) =>
          this.deleteFile({ id: createdFile.id }, { showLoader: false }).catch(console.error)
        ));
        if (createdFiles.length) {
          uploadDiagnosticLog("info", "entry-create-file-prefetch-cleaned-up", {
            uploadJobId: jobId,
            chunkIndex,
            entryCount: createdFiles.length,
          });
        }
      } catch (error) {
        uploadDiagnosticLog("warn", "entry-create-file-prefetch-cleanup-failed", {
          uploadJobId: jobId,
          chunkIndex,
        }, error);
      }
    },
    async finaliseFileBatchForChunk(jobId, results, isCancelled = null) {
      const successfulUploads = results.filter((result) => result.success && result.entry && result.updates?.itemId);
      if (!successfulUploads.length) {
        return [];
      }
      if (await this.isUploadJobCancelled(jobId, isCancelled)) {
        uploadDiagnosticLog("info", "entry-finalise-file-batch-skipped-job-stopped", {
          uploadJobId: jobId,
          entryCount: successfulUploads.length,
        });
        return [];
      }
      const startedAt = uploadDiagnosticStart();
      let response = null;
      try {
        response = await this.finaliseFilesBatch(successfulUploads.map(({ entry, updates }) => ({
          id: updates.itemId,
          uploadJobId: jobId,
          uploadJobLogId: entry.serverLogId,
          expectedCrc32c: updates.expectedCrc32c,
        })));
      } catch (error) {
        if (isStoppedUploadJobError(error)) {
          await updateUploadJob(jobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.ABANDONED }).catch(console.error);
        }
        if (isStoppedUploadJobError(error) || await this.isUploadJobCancelled(jobId, isCancelled)) {
          uploadDiagnosticLog("info", "entry-finalise-file-batch-stopped-before-fallback", {
            uploadJobId: jobId,
            entryCount: successfulUploads.length,
          }, error);
          return [];
        }
        uploadDiagnosticLog("warn", "entry-finalise-file-batch-api-failed-falling-back", {
          uploadJobId: jobId,
          entryCount: successfulUploads.length,
          uploadJobLogIds: successfulUploads.map(({ entry }) => entry.serverLogId).filter(Boolean).slice(0, 50),
          clientEntryIds: successfulUploads.map(({ entry }) => entry.clientEntryId).slice(0, 50),
          itemIds: successfulUploads.map(({ updates }) => updates.itemId).filter(Boolean).slice(0, 50),
        }, error);
        return await this.finaliseFilesIndividuallyForChunk(jobId, successfulUploads, startedAt, isCancelled);
      }
      if (await this.isUploadJobCancelled(jobId, isCancelled)) {
        uploadDiagnosticLog("info", "entry-finalise-file-batch-stopped", {
          uploadJobId: jobId,
          entryCount: successfulUploads.length,
        });
        return [];
      }
      const durationMs = uploadDiagnosticDurationMs(startedAt);
      const finalisedByItemId = new Map((response?.entries || []).map((entry) => [String(entry.id), entry]));
      const finalised = [];
      for (const uploadResult of successfulUploads) {
        const finaliseResult = finalisedByItemId.get(String(uploadResult.updates.itemId));
        if (!finaliseResult?.success) {
          if (await this.isUploadJobCancelled(jobId, isCancelled)) {
            return finalised;
          }
          const error = new Error(finaliseResult?.errorMessage || "Batch finalise failed");
          error.code = finaliseResult?.errorCode || "UPLOAD_FINALISE_FAILED";
          if (!this.shouldPreserveChunkedRetryItem(uploadResult.entry)) {
            await this.deleteFile({ id: uploadResult.updates.itemId }, { showLoader: false }).catch(console.error);
          }
          await this.recordEntryFailure(jobId, uploadResult.entry, error);
          continue;
        }
        this.recordUploadTiming("finaliseFile", {
          durationMs: Math.round(durationMs / Math.max(1, successfulUploads.length)),
          size: uploadResult.entry.size,
          clientEntryId: uploadResult.entry.clientEntryId,
        });
        finalised.push(uploadResult);
      }
      uploadDiagnosticLog("info", "entry-finalise-file-batch-complete", {
        uploadJobId: jobId,
        entryCount: successfulUploads.length,
        successCount: finalised.length,
        durationMs,
      });
      return finalised;
    },
    async finaliseFilesIndividuallyForChunk(jobId, successfulUploads, batchStartedAt, isCancelled = null) {
      const finalised = [];
      await Promise.all(successfulUploads.map(async (uploadResult) => {
        if (await this.isUploadJobCancelled(jobId, isCancelled)) {
          return;
        }
        try {
          await this.finaliseFile({
            id: uploadResult.updates.itemId,
            uploadJobId: jobId,
            uploadJobLogId: uploadResult.entry.serverLogId,
            expectedCrc32c: uploadResult.updates.expectedCrc32c,
          });
          this.recordUploadTiming("finaliseFile", {
            durationMs: uploadDiagnosticDurationMs(batchStartedAt),
            size: uploadResult.entry.size,
            clientEntryId: uploadResult.entry.clientEntryId,
          });
          finalised.push(uploadResult);
        } catch (error) {
          if (isStoppedUploadJobError(error)) {
            await updateUploadJob(jobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.ABANDONED }).catch(console.error);
            return;
          }
          if (await this.isUploadJobCancelled(jobId, isCancelled)) {
            return;
          }
          if (!this.shouldPreserveChunkedRetryItem(uploadResult.entry)) {
            await this.deleteFile({ id: uploadResult.updates.itemId }, { showLoader: false }).catch(console.error);
          }
          await this.recordEntryFailure(jobId, uploadResult.entry, error);
        }
      }));
      uploadDiagnosticLog("info", "entry-finalise-file-single-fallback-complete", {
        uploadJobId: jobId,
        entryCount: successfulUploads.length,
        successCount: finalised.length,
        durationMs: uploadDiagnosticDurationMs(batchStartedAt),
      });
      return finalised;
    },
    uploadFileOptionsForEntry(jobId, entry, file, extra = {}) {
      const item = entry.sourceItem;
      return {
        parentItem: { id: this.folderId },
        name: item.file.name,
        subPath: item.path,
        retentionSchedule: this.selectedRetentionSchedule.id,
        file,
        size: entry.size ?? file?.size ?? 0,
        contentType: entry.mimeType || file?.type || item.file?.type,
        isSecret: this.isSecret,
        tags: this.currentSubjects,
        metadata: this.currentMetadata,
        redsMetadata: entry.redsMetadata || null,
        uploadJobId: jobId,
        uploadJobLogId: entry.serverLogId,
        clientEntryId: entry.clientEntryId,
        uploadV2Active: true,
        overwriteExisting: entry.overwriteExisting === true,
        ...extra,
      };
    },
    shouldPreserveChunkedRetryItem(entry) {
      return entry?.entryType === UPLOAD_ENTRY_TYPE.FILE && Number(entry.size || 0) >= UPLOAD_CHUNKED_MIN_SIZE;
    },
    uploadFileEntryV2(jobId, entry, itemIndex, progressCallback, precreatedFileResponse = null) {
      const item = entry.sourceItem;
      let activeUpload = null;
      return createCancellableRunner(async (isCancelled) => {
        const file = item.sourceHandle ? await item.sourceHandle.getFile() : item.file;
        if (isCancelled()) {
          return;
        }
        activeUpload = this.uploadFile(this.uploadFileOptionsForEntry(jobId, entry, file, {
          precreatedFileResponse,
          skipFinalise: !!precreatedFileResponse,
          onClientProgress: (info = {}) => {
            this.persistEntryProgress(jobId, entry, info);
            uploadDiagnosticLogThrottled("debug", "entry-upload-progress", `${jobId}:${entry.clientEntryId}`, {
              uploadJobId: jobId,
              uploadJobLogId: entry.serverLogId,
              clientEntryId: entry.clientEntryId,
              itemId: entry.itemId,
              uploadedBytes: info.uploadedBytes,
              totalBytes: info.totalBytes,
              chunkIndex: info.chunkIndex,
            });
          },
          onClientStatus: (status, updates = {}) => {
            this.queueEntryStatusPersist(jobId, entry, status, updates);
          },
          onClientTiming: (stage, timing = {}) => {
            this.recordUploadTiming(stage, {
              ...timing,
              clientEntryId: entry.clientEntryId,
            });
          },
        }), itemIndex, progressCallback);
        const uploadResult = await activeUpload.run();
        return uploadResult?.result;
      }, async () => {
        await activeUpload?.cancel();
      });
    },
      async uploadFileEntriesV2(jobId, manifest, progressCallback, errorCallback, isCancelled, registerCancel) {
        const fileEntries = manifest.entries.filter((entry) => entry.entryType === UPLOAD_ENTRY_TYPE.FILE);
        const createChunks = this.chunkArray(fileEntries, UPLOAD_V2_CREATE_BATCH_SIZE);
        let anySuccess = false;
        let sensitiveDetections = [];
        const filePhaseStartedAt = Date.now();
      const createBatchPromises = new Array(createChunks.length).fill(null);
      const scheduledClientEntryIds = new Set();
      const activeUploads = new Set();
      const finalisePromises = [];
        let finaliseTimer = null;
        let pendingFinaliseResults = [];
        const fileProgress = new Array(fileEntries.length).fill(0);
        const startCreateBatch = (chunkIndex) => {
          if (chunkIndex < 0 || chunkIndex >= createChunks.length || createBatchPromises[chunkIndex]) {
            return createBatchPromises[chunkIndex];
          }
          const chunk = createChunks[chunkIndex];
          this.queueFileBatchForUpload(jobId, chunk, chunkIndex);
          createBatchPromises[chunkIndex] = this.createFileBatchForChunk(jobId, chunk, isCancelled);
        uploadDiagnosticLog("debug", "entry-create-file-batch-prefetch-started", {
          uploadJobId: jobId,
          chunkIndex,
          entryCount: chunk.length,
        });
        return createBatchPromises[chunkIndex];
      };
      const adaptiveCreatePrefetchWindow = () => {
        const activeCount = activeUploads.size;
        const lowWatermark = Math.max(1, Math.floor(UPLOAD_V2_FILE_CONCURRENCY / 2));
        return activeCount <= lowWatermark ? CREATE_PREFETCH_MAX_BATCHES : CREATE_PREFETCH_MIN_BATCHES;
      };
      const prefetchCreateBatches = (chunkIndex, reason) => {
        const windowSize = adaptiveCreatePrefetchWindow();
        for (let offset = 0; offset <= windowSize; offset++) {
          startCreateBatch(chunkIndex + offset);
        }
        uploadDiagnosticLog("debug", "entry-create-file-adaptive-prefetch", {
          uploadJobId: jobId,
          chunkIndex,
          reason,
          windowSize,
          activeStorageUploads: activeUploads.size,
          createBatchSize: UPLOAD_V2_CREATE_BATCH_SIZE,
        });
      };
      const refreshWorkerWatchdogState = (reason) => {
          this.touchUploadWorkerActivity(reason, {
            nextEntryIndex,
            activeStorageUploads: activeUploads.size,
            completedStorageUploads: fileProgress.filter((value) => value >= 1).length,
            finaliseInFlight: finalisePromises.length,
            pendingCreateBatches: createBatchPromises.filter(Boolean).length,
          });
        };

      const reportFileProgress = (overallIndex, fileName, fraction) => {
        fileProgress[overallIndex] = Math.max(fileProgress[overallIndex] || 0, Math.max(0, Math.min(1, Number(fraction || 0))));
        progressCallback(fileProgress.reduce((total, value) => total + value, 0), fileName);
      };
        const cleanupPendingCreatedFiles = async () => {
          await Promise.all(createBatchPromises
            .map((promise, index) => promise ? this.cleanupPrecreatedFileBatch(jobId, index, promise, scheduledClientEntryIds) : null)
            .filter(Boolean));
        };
        const finaliseUploadResults = async (results, batchLabel = "mini-batch") => {
          refreshWorkerWatchdogState("finalise-start");
          try {
            if (await this.isUploadJobCancelled(jobId, isCancelled)) {
              return [];
            }
            const finalisedResults = await this.finaliseFileBatchForChunk(jobId, results, isCancelled);
            finalisedResults.forEach(({ entry, updates }) => {
              this.applyEntryStatus(entry, CLIENT_UPLOAD_STATUS.COMPLETE, updates, jobId);
            uploadDiagnosticLog("debug", "entry-upload-complete", {
              uploadJobId: jobId,
              uploadJobLogId: entry.serverLogId,
              clientEntryId: entry.clientEntryId,
              itemId: updates.itemId,
              size: entry.size,
            });
          });
          const successfulFinalisedResults = finalisedResults.filter((result) => result.success);
          successfulFinalisedResults.forEach(({ entry }) => {
            const key = `${jobId}:${entry.clientEntryId}`;
            delete this.pendingUploadStatuses?.[key];
            delete this.pendingUploadProgress?.[key];
          });
          await updateUploadEntries(jobId, successfulFinalisedResults.map(({ entry, updates }) => ({
            clientEntryId: entry.clientEntryId,
            updates: {
              clientStatus: CLIENT_UPLOAD_STATUS.COMPLETE,
              itemId: updates.itemId,
              uploadId: updates.uploadId,
              uploadedBytes: entry.size,
              progressPercent: 100,
              progressUpdatedAt: new Date().toISOString(),
            },
          }))).catch((error) => {
              uploadDiagnosticLog("warn", "entry-batch-local-update-failed", {
                uploadJobId: jobId,
                batchLabel,
                entryCount: results.filter((result) => result?.success).length,
              }, error);
              console.error(error);
            });
          anySuccess = anySuccess || finalisedResults.some((result) => result.success);
          await this.persistLocalJobSummary(jobId).catch(console.error);
          sensitiveDetections = sensitiveDetections.concat(
            finalisedResults
              .map((result) => result.sensitiveNameDetection)
              .filter((detection) => detection?.sensitiveNameDetected)
            );
            return finalisedResults;
          } finally {
            refreshWorkerWatchdogState("finalise-complete");
          }
        };
        const clearFinaliseTimer = () => {
          if (finaliseTimer) {
            window.clearTimeout(finaliseTimer);
            finaliseTimer = null;
          }
        };
        const flushPendingFinaliseResults = (reason = "manual") => {
          clearFinaliseTimer();
          if (!pendingFinaliseResults.length) {
            return null;
          }
          const results = pendingFinaliseResults.splice(0, FINALISE_BATCH_SIZE);
          const promise = finaliseUploadResults(results, reason)
            .finally(() => {
              const index = finalisePromises.indexOf(promise);
              if (index >= 0) {
                finalisePromises.splice(index, 1);
              }
            });
          finalisePromises.push(promise);
          if (pendingFinaliseResults.length) {
            flushPendingFinaliseResults(`${reason}-overflow`);
          }
          return promise;
        };
        const scheduleFinaliseFlush = () => {
          if (pendingFinaliseResults.length >= FINALISE_BATCH_SIZE) {
            flushPendingFinaliseResults("count");
            return;
          }
          if (!finaliseTimer) {
            finaliseTimer = window.setTimeout(() => {
              finaliseTimer = null;
              flushPendingFinaliseResults("timer");
            }, FINALISE_BATCH_DELAY_MS);
          }
        };
        const completeFileEntry = (result) => {
          refreshWorkerWatchdogState("storage-terminal");
          if (result?.success) {
            pendingFinaliseResults.push(result);
            scheduleFinaliseFlush();
          }
        };

      let nextEntryIndex = 0;
      let cancelled = false;
      if (await this.isUploadJobCancelled(jobId, isCancelled)) {
        return { anySuccess, sensitiveDetections, cancelled: true };
      }
      registerCancel(() => Promise.all(Array.from(activeUploads).map((upload) => upload.cancel())));
      this.startUploadWorkerWatchdog(jobId, fileEntries.length);
      prefetchCreateBatches(0, "initial");
      refreshWorkerWatchdogState("create-prefetch-start");
      const uploadWorker = async () => {
        while (!cancelled) {
          const overallIndex = nextEntryIndex;
          if (overallIndex >= fileEntries.length) {
            return;
          }
          nextEntryIndex += 1;
          refreshWorkerWatchdogState("worker-claimed-entry");
          const chunkIndex = Math.floor(overallIndex / UPLOAD_V2_CREATE_BATCH_SIZE);
          const entry = fileEntries[overallIndex];
          if (!entry) {
            uploadDiagnosticLog("warn", "entry-upload-worker-missing-entry", {
              uploadJobId: jobId,
              overallIndex,
              fileEntryCount: fileEntries.length,
            });
            return;
          }
          if (await this.isUploadJobCancelled(jobId, isCancelled)) {
            await this.persistEntryStatus(jobId, entry, CLIENT_UPLOAD_STATUS.CANCELLED);
            cancelled = true;
            return;
          }
          let createdFilesByClientId = new Map();
          prefetchCreateBatches(chunkIndex, "worker-claim");
          try {
            createdFilesByClientId = await createBatchPromises[chunkIndex];
            refreshWorkerWatchdogState("create-batch-ready");
            if (await this.isUploadJobCancelled(jobId, isCancelled)) {
              cancelled = true;
              return;
            }
          } catch (error) {
            if (await this.isUploadJobCancelled(jobId, isCancelled)) {
              cancelled = true;
              return;
            }
              uploadDiagnosticLog("warn", "entry-create-file-batch-failed", {
                uploadJobId: jobId,
                chunkIndex,
                entryCount: createChunks[chunkIndex]?.length || 0,
              }, error);
              await this.recordEntryFailure(jobId, entry, error);
              reportFileProgress(overallIndex, "", 1);
              completeFileEntry({ success: false });
              continue;
          }
          if (!await this.isUploadJobCancelled(jobId, isCancelled)) {
            prefetchCreateBatches(chunkIndex + 1, "create-ready");
            refreshWorkerWatchdogState("create-prefetch-start");
          } else {
            cancelled = true;
            return;
          }
            if (await this.isUploadJobCancelled(jobId, isCancelled)) {
              await this.persistEntryStatus(jobId, entry, CLIENT_UPLOAD_STATUS.CANCELLED);
              completeFileEntry({ success: false, cancelled: true });
              cancelled = true;
              return;
            }
          const createdFile = createdFilesByClientId.get(entry.clientEntryId);
          if (!createdFile?.success) {
            if (createdFile?.stopped || await this.isUploadJobCancelled(jobId, isCancelled)) {
              cancelled = true;
              return;
            }
            const error = new Error(createdFile?.errorMessage || "Batch create failed");
              error.code = createdFile?.errorCode || "UPLOAD_CREATE_FAILED";
              await this.recordEntryFailure(jobId, entry, error);
              reportFileProgress(overallIndex, "", 1);
              completeFileEntry({ success: false });
              continue;
            }
          this.recordUploadTiming("queueWait", {
            durationMs: Date.now() - filePhaseStartedAt,
            size: entry.size,
            clientEntryId: entry.clientEntryId,
          });
          const upload = this.uploadFileEntryV2(jobId, entry, overallIndex, (currentIndex, fileName) => {
            reportFileProgress(overallIndex, fileName, Number(currentIndex || 0) - overallIndex);
          }, createdFile);
          scheduledClientEntryIds.add(entry.clientEntryId);
          activeUploads.add(upload);
          refreshWorkerWatchdogState("storage-start");
          this.recordEffectiveConcurrency(activeUploads.size, Math.max(0, fileEntries.length - nextEntryIndex));
          try {
            const uploadResult = await upload.run();
            activeUploads.delete(upload);
            refreshWorkerWatchdogState("storage-run-complete");
            if (uploadResult?.cancelled || await this.isUploadJobCancelled(jobId, isCancelled)) {
                completeFileEntry({ success: false, cancelled: true });
                cancelled = true;
                return;
              }
            const updates = {
              itemId: uploadResult?.result?.id,
              uploadId: uploadResult?.result?.uploadId,
              expectedCrc32c: uploadResult?.result?.expectedCrc32c,
              uploadedBytes: entry.size,
              totalBytes: entry.size,
            };
            delete this.pendingUploadProgress[`${jobId}:${entry.clientEntryId}`];
            uploadDiagnosticLog("debug", "entry-storage-upload-complete", {
              uploadJobId: jobId,
              uploadJobLogId: entry.serverLogId,
              clientEntryId: entry.clientEntryId,
              itemId: updates.itemId,
              size: entry.size,
              });
              reportFileProgress(overallIndex, "", 1);
              completeFileEntry({
                success: true,
                entry,
                updates,
              sensitiveNameDetection: uploadResult?.result?.sensitiveNameDetection || null,
            });
          } catch (error) {
            activeUploads.delete(upload);
              refreshWorkerWatchdogState("storage-run-error");
              if (await this.isUploadJobCancelled(jobId, isCancelled)) {
                cancelled = true;
                return;
              }
              reportFileProgress(overallIndex, "", 1);
              await this.recordEntryFailure(jobId, entry, error);
              errorCallback(entry.sourceItem, classifyUploadError(error));
              completeFileEntry({ success: false });
            } finally {
            this.recordEffectiveConcurrency(activeUploads.size, Math.max(0, fileEntries.length - nextEntryIndex));
          }
        }
      };

      const workers = Array.from(
        { length: Math.min(UPLOAD_V2_FILE_CONCURRENCY, fileEntries.length) },
        () => uploadWorker()
      );
        try {
          await Promise.all(workers);
          flushPendingFinaliseResults("file-phase-complete");
          if (cancelled || await this.isUploadJobCancelled(jobId, isCancelled)) {
            await cleanupPendingCreatedFiles();
            await Promise.all(finalisePromises);
            return { anySuccess, sensitiveDetections, cancelled: true };
          }
          await Promise.all(finalisePromises);
          return { anySuccess, sensitiveDetections, cancelled: false };
        } finally {
          clearFinaliseTimer();
          refreshWorkerWatchdogState("file-phase-stop");
        this.stopUploadWorkerWatchdog();
      }
    },
    uploadFilesV2(progressCallback, errorCallback) {
      let cancelCurrentWork = null;
      return createCancellableRunner(async (isCancelled) => {
        let anySuccess = false;
        let sensitiveDetections = [];
        const executionStartedAt = uploadDiagnosticStart();
        progressCallback(0, "", this.chosenFiles.length, "building-manifest", {
          current: 0,
          total: this.chosenFiles.length,
        });
        await yieldToBrowser();
        const manifest = this.buildUploadJobManifest();
        const response = await this.createAndPersistUploadJob(manifest, progressCallback);
        const jobId = response.jobId;

        progressCallback(0, "", manifest.summary.fileCount, "starting-upload", {
          current: 0,
          total: manifest.summary.entryCount,
        });
        await this.startLocalLease(jobId);
        this.startUploadJobKeepAlive(jobId);
        await updateUploadJob(jobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.QUEUED }).catch(console.error);
        this.emitUploadJobChanged({ status: "IN_PROGRESS" });
        uploadDiagnosticLog("info", "upload-execution-start", {
          uploadJobId: jobId,
          targetFolderId: manifest.folderId,
          entryCount: manifest.summary.entryCount,
          fileCount: manifest.summary.fileCount,
          folderCount: manifest.summary.folderCount,
          totalBytes: manifest.summary.totalBytes,
        });

        const folderResult = await this.uploadFolderEntriesV2(jobId, manifest, progressCallback, isCancelled);
        if (folderResult.cancelled) {
          return { anySuccess, sensitiveDetections, cancelled: true };
        }
        sensitiveDetections = sensitiveDetections.concat(
          folderResult.sensitiveDetections.filter((detection) => detection?.sensitiveNameDetected)
        );

        const itemProgressCallback = (currentIndex, fileName) =>
          progressCallback(currentIndex, fileName, manifest.summary.fileCount, "uploading-files");

        const fileResult = await this.uploadFileEntriesV2(
          jobId,
          manifest,
          itemProgressCallback,
          errorCallback,
          isCancelled,
          (cancel) => {
            cancelCurrentWork = cancel;
          }
        );
        anySuccess = fileResult.anySuccess;
        if (fileResult.cancelled) {
          return { anySuccess, sensitiveDetections, cancelled: true };
        }
        sensitiveDetections = sensitiveDetections.concat(fileResult.sensitiveDetections);
        const hasEntryErrors = manifest.entries.some((entry) => entry.clientStatus === CLIENT_UPLOAD_STATUS.ERROR);

        if (await this.isUploadJobCancelled(jobId, isCancelled)) {
          await this.persistLocalJobSummary(jobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.CANCELLED }).catch(console.error);
          return { anySuccess, sensitiveDetections, cancelled: true };
        }

        await this.persistLocalJobSummary(jobId, {
          clientJobStatus: hasEntryErrors
            ? (anySuccess ? CLIENT_UPLOAD_STATUS.COMPLETE_WITH_ERRORS : CLIENT_UPLOAD_STATUS.ERROR)
            : CLIENT_UPLOAD_STATUS.COMPLETE,
        }).catch(console.error);
        this.stopUploadJobKeepAlive();
        await this.completeUploadJob(jobId);
        this.emitUploadJobChanged({ status: hasEntryErrors ? "COMPLETE_WITH_ERRORS" : "COMPLETE" });
        uploadDiagnosticLog(hasEntryErrors ? "warn" : "info", "upload-execution-complete", {
          uploadJobId: jobId,
          status: hasEntryErrors ? "COMPLETE_WITH_ERRORS" : "COMPLETE",
          anySuccess,
          entryCount: manifest.summary.entryCount,
          errorCount: manifest.entries.filter((entry) => entry.clientStatus === CLIENT_UPLOAD_STATUS.ERROR).length,
          durationMs: uploadDiagnosticDurationMs(executionStartedAt),
        });

        if (!anySuccess) {
          throw new Error("Failed to upload");
        }
        return {
          anySuccess,
          sensitiveDetections,
          cleanupLocalJob: !hasEntryErrors,
          cleanupSuccessfulEntries: hasEntryErrors && anySuccess,
        };
      }, async () => {
        if (this.uploadJobId) {
          uploadDiagnosticLog("info", "upload-cancel-requested", { uploadJobId: this.uploadJobId });
          await markLocalUploadJobCancelling(this.uploadJobId).catch(console.error);
          await this.persistLocalJobSummary(this.uploadJobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.CANCELLING }).catch(console.error);
        }
        await cancelCurrentWork?.();
        if (this.uploadJobId) {
          await cancelLocalUploadJob(this.uploadJobId).catch(console.error);
          await this.markRemainingEntriesCancelled(this.uploadJobId);
          await this.persistLocalJobSummary(this.uploadJobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.CANCELLED }).catch(console.error);
          await this.flushCancellationProgressTelemetry(this.uploadJobId).catch(console.error);
          await updateUploadJob(this.uploadJobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.CANCELLED }).catch(console.error);
          await this.cancelUploadJob(this.uploadJobId).catch(console.error);
          this.emitUploadJobChanged({ status: "CANCELLED" });
        }
        this.stopUploadJobKeepAlive();
      });
    },
    async startUpload() {
      if (this.state === this.STATE.UPLOADING || this.state === this.STATE.CANCELLING) {
        return;
      }
      if (!this.uploadStoragePreflightReady && !await this.runStoragePreflight()) {
        return;
      }
      if (!this.validateUploadV2FileCount(this.chosenFiles || [], { showLargeSelectionWarning: false })) {
        return;
      }
      this.cancelRequested = false;
      this.suppressUploadErrors = false;
      this.resetUploadTransferStats?.();
      this.setState(this.STATE.UPLOADING);
      try {
        this.managerUploadId = enqueueUpload({
          folderId: this.folderId,
          folder: this.folder,
          chosenFiles: this.chosenFiles,
          retryManifest: this.retryManifest,
          isSecret: this.isSecret,
          selectedRetentionSchedule: { ...this.selectedRetentionSchedule },
          currentSubjects: [...this.currentSubjects],
          currentMetadata: [...this.currentMetadata],
        });
        this.syncManagerUploadState(uploadManagerState.activeUpload);
      } catch (error) {
        console.error(error);
        this.showError(error.message || this.$t("upload-error", { filename: "" }), { expiryTime: 30000 });
        this.setState(this.STATE.FILE_OPTIONS);
      }
    },
    syncManagerUploadState(upload) {
      if (!upload || upload.localUploadId !== this.managerUploadId) {
        return;
      }
      this.uploadJobId = upload.uploadJobId;
      if (this.uploadJobId) {
        const terminalStatus = upload.terminalStatus || (upload.hasEntryErrors ? "COMPLETE_WITH_ERRORS" : null);
        this.emitUploadJobChanged({
          status: upload.status === "complete"
            ? (terminalStatus || "COMPLETE")
            : upload.status === "cancelled"
              ? "CANCELLED"
              : upload.status === "cancelling"
                ? "CANCELLING"
                : upload.status === "failed"
                  ? "FAILED"
                  : "IN_PROGRESS",
          totalBytes: upload.totalBytes,
          uploadedBytes: upload.uploadedBytes,
          progressPercent: upload.progressPercent,
          totalEntryCount: upload.totalEntryCount,
          completedEntryCount: upload.completedEntryCount,
          failedEntryCount: upload.failedEntryCount,
          totalFileCount: upload.totalFileCount,
          completedFileCount: upload.completedFileCount,
          failedFileCount: upload.failedFileCount,
          totalFolderCount: upload.totalFolderCount,
          completedFolderCount: upload.completedFolderCount,
          failedFolderCount: upload.failedFolderCount,
        });
      }
      this.currentItem = upload.currentItem;
      this.currentFilename = upload.currentFilename;
      this.currentPhase = upload.currentPhase || "uploading-files";
      this.currentPhaseProgress = upload.currentPhaseProgress || { current: 0, total: 0 };
      this.totalFolders = upload.totalFolders || 0;
      this.foldersDone = upload.foldersDone || 0;
      if (Number(upload.totalBytes || 0) > 0) {
        this.updateOverallUploadByteProgress?.(upload.uploadedBytes, upload.totalBytes);
      } else if (upload.progressPercent !== null && upload.progressPercent !== undefined) {
        this.progress = Math.max(0, Math.min(1, Number(upload.progressPercent || 0) / 100));
      } else {
        this.progress = upload.progress || 0;
      }
      this.uploadTransferRateBytesPerSecond = upload.uploadTransferRateBytesPerSecond || null;
      if (upload.status === "complete") {
        this.setState(this.STATE.DONE);
        if (!upload.hasEntryErrors && upload.terminalStatus !== "COMPLETE_WITH_ERRORS") {
          setTimeout(() => this.returnToBrowse(), 1000);
        }
      } else if (upload.status === "failed") {
        this.setState(this.STATE.FILE_OPTIONS);
      } else if (upload.status === "cancelled") {
        this.setState(this.STATE.CANCELLED);
      } else if (upload.status === "cancelling") {
        this.setState(this.STATE.CANCELLING);
      } else {
        this.setState(this.STATE.UPLOADING);
      }
    },
    async cancelUpload(shouldNavigate = true) {
      if (this.managerUploadId) {
        this.cancelRequested = true;
        this.suppressUploadErrors = true;
        this.setState(this.STATE.CANCELLING);
        if (this.uploadJobId) {
          await markLocalUploadJobCancelling(this.uploadJobId).catch(console.error);
        }
        await requestUploadCancel(this.managerUploadId).catch((error) => {
          uploadDiagnosticLog("info", "upload-cancel-request-settled-with-error", {
            uploadJobId: this.uploadJobId,
            localUploadId: this.managerUploadId,
          }, error);
        });
        this.setState(this.STATE.CANCELLED);
      }
      if (shouldNavigate) {
        this.returnToBrowse();
      }
    },
  },
  mounted() {
    if (process.env.NODE_ENV !== "test") {
      this.runStoragePreflight({ notifyOnFailure: false }).catch(console.error);
    }
  },
  beforeDestroy() {
    this.stopProgressFlushTimer?.();
    this.stopStatusFlushTimer?.();
    this.stopLocalLease?.();
  },
};
</script>

<template>
  <div class="upload upload-v2">
    <section v-if="state === STATE.FILE_PICK && uploadStoragePreflightChecking" class="upload-preflight-status">
      {{ $t("upload-storage-preflight-checking") }}
    </section>
    <section v-if="state === STATE.FILE_PICK && uploadStoragePreflightFailed" class="upload-preflight-error">
      <span>{{ $t("upload-storage-preflight-failed") }}</span>
      <button type="button" @click.prevent="runStoragePreflight()">
        {{ $t("retry") }}
      </button>
    </section>
    <FilePicker
      v-if="state === STATE.FILE_PICK"
      :key="`file-${pickerResetKey}`"
      :disabled="uploadPickerDisabled"
      @filesReady="filesReadyHandler"
    />
    <FolderPicker
      v-if="state === STATE.FILE_PICK"
      :key="`folder-${pickerResetKey}`"
      :disabled="uploadPickerDisabled"
      @filesReady="filesReadyHandler"
    />

    <section class="options" v-if="state >= STATE.FILE_OPTIONS && showUploadSetupControls">
      <div>
        <h5 class="label">{{ $t("files-selected") }}</h5>
        <p class="value">{{ (this.chosenFiles || []).length }}</p>
      </div>
      <div>
        <h5 class="label">{{ $t("total-size") }}</h5>
        <p class="value">{{ collectionFileSize | prettyBytes }}</p>
      </div>
    </section>

    <RetentionSchedule
      v-if="state >= STATE.FILE_OPTIONS && showUploadSetupControls"
      id="retention-schedule"
      :item="folder"
      @retention-schedule-changed="onRetentionScheduleChangedHandler"
    />
    <PropertiesSection :title="$t('confidentiality-heading')" v-if="state >= STATE.FILE_OPTIONS && showUploadSetupControls">
        <label class="confidentiality-label">
          <input type="checkbox" v-model="isSecret">
          {{ $t("confidentiality-label") }}
          <span
            class="help-icon"
            :title="$t('secret-item-tooltip')"
            :aria-label="$t('secret-item-tooltip')"
          >?</span>
        </label>
    </PropertiesSection>
    <PropertiesSection :title="$t('keywords-heading')" :title-help="$t('properties.keywords-help')" v-if="state >= STATE.FILE_OPTIONS && showUploadSetupControls">
      <KeywordSelector
        id="add-tag"
        :allSubjects="allSubjects"
        :selectedTags="currentSubjects"
        :description="$t('keywords-description')"
        alignActionsToPanel
        @tags-changed="tagsChanged"
      />
    </PropertiesSection>

    <Metadata
      :item="metadataItem"
      :metadata="currentMetadata"
      :title-help="$t('properties.metadata-help')"
      @metadata-changed="metadataChanged"
      v-if="state >= STATE.FILE_OPTIONS && showUploadSetupControls"
    />

    <slot name="upload-progress" v-if="state >= STATE.UPLOADING">
      <section class="progress">
        <h5 class="label">
          <template v-if="currentPhase === 'creating-folders'">
            {{ $t("upload-creating-folders") }}
          </template>
          <template v-else>
            {{ $t("uploading", { item: this.currentFilename }) }}
          </template>
        </h5>
        <p v-if="state === STATE.DONE">{{ $t("upload-complete") }}</p>
        <p v-else-if="currentPhase === 'creating-folders'">
          {{ $t("upload-creating-folders-progress", {current: (currentPhaseProgress || {}).current, total: (currentPhaseProgress || {}).total}) }}
        </p>
        <p v-else>{{ $t("upload-progress", {current: currentItem + 1, total: (this.chosenFiles || []).length}) }}</p>
        <m-linear-progress :buffer="progress > 0 ? 1 : 0" :open="true" :progress="progress"/>
      </section>
    </slot>

    <section class="buttons" v-if="state <= STATE.UPLOADING">
      <button class="primary" :disabled="!selectedRetentionSchedule || state === STATE.UPLOADING" @click="startUpload">
        {{ $t("begin-upload") }}
      </button>
      <button @click="$emit('confirmCancel')">
        {{ $t("cancel") }}
      </button>
    </section>

    <Popup
      v-if="state === STATE.FILE_OPTIONS && (failedFiles || []).length > 0" :defaultButtons="false"
      @cancelled="clearFailedFiles">
      <template v-slot:header>Failed Files</template>
      <template v-slot:body>
        <div class="failed-files-label">The following files failed to upload:</div>
        <div class="failed-files-block">
          <p v-for="failedFile in failedFiles || []" :key="failedFile.name">{{ failedFile.subPath }}{{failedFile.name}}</p>
        </div>
      </template>
      <template v-slot:footer>
        <div class="buttons">
          <button type="button" class="primary" @click.stop="clearFailedFiles">{{ $t("cancel") }}</button>
        </div>
      </template>
    </Popup>

    <Popup
      v-if="(zeroByteFiles || []).length > 0"
      @cancelled="cancelZeroByteFiles"
      @confirmed="confirmZeroByteFiles"
      icon="warning">
      <template v-slot:header>{{ $t("zero-byte-file-title") }}</template>
      <template v-slot:body>
        <p class="zero-byte-upload-message">{{ formatZeroByteUploadMessage(zeroByteFiles) }}</p>
        <div class="failed-files-block">
          <p v-for="item in zeroByteFiles" :key="zeroByteFileLabel(item)">{{ zeroByteFileLabel(item) }}</p>
        </div>
      </template>
      <template v-slot:confirmLabel>{{ $t("zero-byte-file-confirm") }}</template>
    </Popup>
  </div>
</template>

<style lang="scss" scoped>
.upload {
  padding-top: 1rem;
}

h5 {
  font-weight: bold;
  margin-bottom: 1rem;
}

.options {
  display: flex;
  background: white;
  border: 1px solid #C8D2DC;
  border-radius: 8px;
  box-sizing: border-box;
  font-size: 0.875rem;
  margin-bottom: 1rem;

  > div {
    padding: 1.5rem;

    &:not(:last-child) {
      border-right: 1px solid #E0E0E0;
    }
  }
}

.required-field:after {
  content:" *";
  color: red;
}

.confidentiality, .tags {
  > h5 {
    margin: 2rem 1rem 0;
  }

  > section {
    display: flex;
    flex-direction: column;
    margin: 1rem 0;
    background: white;
    padding: 1.5rem;

    label {
      margin-left: 1rem;
      font-weight: bold;
    }

    input {
      margin-right: 0.5rem;
    }
  }

  select {
    margin: 1rem 0
  }

  .warning {
    color: #EF0000;
  }
}

.help-icon {
  align-items: center;
  background: transparent;
  border: 1px solid #1275CC;
  border-radius: 50%;
  color: #1275CC;
  cursor: help;
  display: inline-flex;
  flex: 0 0 1.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  height: 1.25rem;
  justify-content: center;
  line-height: 1;
  margin-left: 0.35rem;
  padding: 0;
  width: 1.25rem;
}

.progress {
  padding: 1.5rem 1.5rem 2rem;
  background: white;
  font-size: 0.875rem;
  border-top: 1px solid #E0E0E0;
}

.buttons {
  margin-top: 1rem;
}

.mdc-select,
.mdc-text-field {
  background-color: white;
  padding-left: 0;
  border-radius: 0;
}

.mdc-select .mdc-select__native-control,
.mdc-select .mdc-floating-label {
  padding-left: 0.5rem;
}

input {
  background-color: #fff;
  padding-top: 0;
  padding-left: 0.1rem;
}

.relative-dates a.chip-trigger {
  display: block;
  padding: 0.5rem;
}

.relative-dates .mdc-chip {
  padding: 0;
}

.buttons button {
  margin-right: 1rem;
}

.buttons button {
  background: transparent;
  border: 1px solid #1275CC;
  color: #1275CC;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-weight: bold;
  cursor: pointer;
  line-height: 1.6rem;

  &:disabled {
    cursor: default;
    opacity: 0.2;
  }

  &.primary {
    background: #1275CC;
    border-color: transparent;
    color: white;
  }

  > i {
    vertical-align: -0.35rem;
    padding-right: 0.25rem;
  }
}

.failed-files-block {
  overflow: auto;
  height: 100px;
  border: 1px solid rgb(118, 118, 118);
  padding: 0.5rem;
}

.failed-files-label {
  margin-bottom: 0.5rem;
}

.zero-byte-upload-message {
  margin-bottom: 1.5rem;
}

.upload.upload-v2 {
  .selection-processing {
    margin-top: 0.75rem;
    border: 1px solid #C8D2DC;
    border-radius: 8px;
    box-sizing: border-box;
  }

  .upload-preflight-status,
  .upload-preflight-error {
    align-items: center;
    border-radius: 4px;
    box-sizing: border-box;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    padding: 0.75rem 1rem;
  }

  .upload-preflight-status {
    background: #EEF6FC;
    border: 1px solid #B6D8F2;
    color: #1F4E79;
  }

  .upload-preflight-error {
    background: #FFF4F2;
    border: 1px solid #F0B8AF;
    color: #8A1F11;

    button {
      background: white;
      border: 1px solid #8A1F11;
      border-radius: 4px;
      color: #8A1F11;
      cursor: pointer;
      font-weight: bold;
      padding: 0.5rem 0.75rem;
    }
  }
}
</style>
